import type { UseWalletUIResult } from 'src/blockchain';

/**
 * On-chain reads for the rental escrow, on whichever chain the deal settles (contracts v3).
 *
 * The renter's projection carries the amounts api mirrored, but two facts about a payment may not
 * come from a projection at all: the exact base-unit total `pay()` will pull, and the token the
 * clone settles in. Both are read from the deployed contract, so the number approved in the wallet
 * is the number the contract takes, and the token approved is the one it will pull.
 *
 * The token especially: it used to be a build constant (`VITE_RENTAL_USDT_CONTRACT_ADDRESS`), which
 * is a TRON address. On Base the same rental settles in USDC or EURC depending on the listing's
 * currency, so a build constant would approve a token that does not exist on the deal's chain —
 * the approve succeeds against nothing and `pay()` reverts with nothing to read.
 */

const TOTAL_REQUIRED = 'getTotalRequired() view returns (uint256)';
const PAYMENT_TOKEN = 'paymentToken() view returns (address)';

export interface RentalContractInfo {
  /** deposit + rental + fee in token base units — what `pay()` pulls, as a decimal string. */
  totalRequired: string;
  /** The token the escrow settles in — the exact token to approve. */
  paymentToken: string;
}

/**
 * Read the pay total and the settlement token from a deployed rental clone.
 *
 * A single retry absorbs read-after-deploy eventual consistency, which both chains have and both
 * express as nonsense rather than an error: a fresh clone answers zeros on TRON and reverts on
 * Base ([[nile-trongrid-eventual-consistency-reads]]). Deploy-on-Pay makes this the normal case
 * here rather than an edge one — the contract is often seconds old when this runs.
 */
export async function readRentalContractInfo(
  walletUI: Pick<UseWalletUIResult, 'readContract'>,
  contractAddress: string,
  chainId?: number
): Promise<RentalContractInfo> {
  const read = async (): Promise<RentalContractInfo> => {
    const [totalRequired, paymentToken] = await walletUI.readContract({
      contractAddress,
      signatures: [TOTAL_REQUIRED, PAYMENT_TOKEN],
      chainId,
    });

    if (!totalRequired || totalRequired === '0' || !paymentToken) {
      throw new Error('Rental contract is not ready yet');
    }

    return { totalRequired, paymentToken };
  };

  try {
    return await read();
  } catch {
    await new Promise((resolve) => setTimeout(resolve, 3000));

    return read();
  }
}

/**
 * The settlement token alone — for the payments that are not the first one (a deposit top-up, the
 * pre-approval an extension needs). Their amounts are quoted by api from the contract's own views,
 * so only the token has to be asked for here.
 */
export async function readRentalPaymentToken(
  walletUI: Pick<UseWalletUIResult, 'readContract'>,
  contractAddress: string,
  chainId?: number
): Promise<string> {
  const [paymentToken] = await walletUI.readContract({
    contractAddress,
    signatures: [PAYMENT_TOKEN],
    chainId,
  });

  if (!paymentToken) {
    throw new Error('Rental contract is not ready yet');
  }

  return paymentToken;
}
